import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Grid, Paper, Typography } from "@mui/material";
import moment from "moment";
import { useEffect } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import {
  getNetSalesPaymentSummary,
  getOrderStats,
  getSalesSummary,
} from "../../reduxStore/slices/reportSlice";
import { formatCurrency } from "../../utils/currency";
import { useNotify } from "../../utils/snackbarUtils";

const SalesSummaryReport = ({ startDate, endDate }) => {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const {
    orderStats,
    salesSummary,
    netSalesSummary,
    loading: isReportLoading,
  } = useSelector((state) => state.report);
  const storeId = user?.storeId;

  const fetchStats = async () => {
    try {
      await dispatch(
        getOrderStats({ storeId, startDate: startDate, endDate: endDate }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  const fetchNetSalesSummary = async () => {
    try {
      await dispatch(
        getNetSalesPaymentSummary({
          storeId,
          startDate: startDate,
          endDate: endDate,
        }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  const fetchSalesSummary = async () => {
    try {
      await dispatch(
        getSalesSummary({ storeId, startDate: startDate, endDate: endDate }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Fetch stats on mount and when storeId changes
  useEffect(() => {
    if (storeId) {
      fetchStats();
      fetchNetSalesSummary();
      fetchSalesSummary();
    }
  }, [storeId, dispatch, startDate, endDate]);

  const tableHead = [
    { id: "date", label: "Date", align: "left" },
    { id: "orders", label: "Orders", align: "left" },
    { id: "grossSales", label: "Gross Sales (₹)", align: "left" },
    { id: "discounts", label: "Discounts (₹)", align: "left" },
    { id: "newSales", label: "Net Sales (₹)", align: "left" },
  ];

  const tableRows = salesSummary.map((item) => ({
    date: moment(item.order_date).format("DD-MMM"),
    orders: item.total_orders,
    grossSales: formatCurrency(item.gross_sales),
    discounts: formatCurrency(item.total_discounts),
    newSales: formatCurrency(item.net_sales),
  }));

  // Summary Cards Data
  const summary = [
    { title: "Total Orders", value: orderStats.total_orders || "0" },
    {
      title: "Gross Sales",
      value: `${formatCurrency(orderStats.gross_sales || "0")}`,
      color: "rgb(0, 171, 85)",
    },
    {
      title: "Discounts",
      value: `${formatCurrency(orderStats.total_discounts || "0")}`,
      color: "error.main",
    },
    {
      title: "Net Sales",
      value: `${formatCurrency(orderStats.net_sales || "0")}`,
      color: "rgb(0, 171, 85)",
    },
  ];

  const chartOptions = {
    chart: {
      id: "sales-trend",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    xaxis: {
      categories: netSalesSummary.map((d) =>
        moment(d.order_date).format("MMM DD, YYYY"),
      ),
    },
    colors: ["#00ab55"],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 4,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val) => formatCurrency(val),
      },
    },
    yaxis: {
      tickAmount: 4,
      decimalsInFloat: 0,
      labels: {
        formatter: (val) => formatCurrency(Math.round(val)).toLocaleString(),
      },
    },
    crosshairs: {
      show: false,
    },
    title: {
      text: "Net Sales Trend",
      align: "left",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
    },
  };

  const chartSeries = [
    {
      name: "Net Sales",
      data: netSalesSummary.map((d) => d.net_sales),
    },
  ];

  const handleSalesExport = () => {
    // Create CSV content
    let csvContent =
      "Date,Orders,Gross Sales (₹),Discounts (₹),Net Sales (₹)\n";
    tableRows.forEach((row) => {
      csvContent += `${row.date},${row.orders},${row.grossSales.replace(/₹|,/g, "")},${row.discounts.replace(/₹|,/g, "")},${row.newSales.replace(/₹|,/g, "")}\n`;
    });

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `sales_summary_${moment().format("YYYYMMDD")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("Sales summary exported successfully.");
    }, 1000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box></Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summary.map((item, index) => (
          <Box
            className="report-count-card"
            key={index}
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Box
              style={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {item.title}
            </Box>
            <Box
              sx={{
                fontWeight: 700,
                fontSize: 20,
                color: item?.color,
                marginTop: "5px",
              }}
            >
              {item.value}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              gap="5px"
              justifyContent="center"
            >
              <Box
                style={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  borderRadius: "50%",
                  position: "relative",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#118D57",
                  background: "rgba(34 197 94 / 16%)",
                }}
              >
                <TrendingUpIcon
                  fontSize="small"
                  sx={{ width: "16px", height: "16px", margin: "0 auto" }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontWeight: 700, mr: "4px" }}>
                  +2.6%
                </Typography>
                last week
              </Typography>
            </Box>
          </Box>
        ))}
      </Grid>

      {/* Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="line"
          height={300}
        />
      </Paper>

      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Sales Details
          </Typography>
          <AppButton
            variant="contained"
            startIcon={<Iconify icon="ic:round-download" />}
            disabled={tableRows.length === 0}
            onClick={handleSalesExport}
          >
            Export
          </AppButton>
        </Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="salesSummaryTables"
          searchPlaceholder="Search By Date"
          searchField={"date"}
        />
      </Box>
    </Box>
  );
};

export default SalesSummaryReport;
